import React, { useEffect, useState, useRef } from 'react';
import classNames from 'classnames';
import proptypes from 'prop-types';
import { Button, Link } from '@one-thd/sui-atomic-components';
import { ShareWeb } from '@one-thd/sui-icons';
import { extend } from '@thd-nucleus/data-sources';
import { ShareProjectDrawer } from './ShareProjectDrawer';
import { isWorkspaceDashboard, triggerAnalytics } from '../helpers';
import {
  PROJECT_SHARE_CLICK,
  PROJECT_DETAILS_SHARE_YOUR_PROJECT,
  PROJECTS_INTERACTION
} from '../constants';

const { bool, func, shape, string } = proptypes;

export const ShareProject = ({
  project, onSuccessMessage, onFailureMessage, showText, showLink, iconSize = null, buttonClassName = null
}) => {
  const [open, setOpen] = useState(false);
  const shareRef = useRef(null);
  const isInWorkspaceDashboard = isWorkspaceDashboard();

  useEffect(() => {
    if (!open && shareRef.current) {
      shareRef.current.blur();
    }
    if (open) {
      const analyticsEvent = isInWorkspaceDashboard
        ? PROJECTS_INTERACTION
        : PROJECT_DETAILS_SHARE_YOUR_PROJECT;
      const analyticsPayload = isInWorkspaceDashboard ? { action: 'open-share-overlay' } : {};
      triggerAnalytics(analyticsEvent, analyticsPayload);
    }
  }, [open]);

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.trigger('ShareProject.ready', {});
  }, []);

  const openDrawer = () => {
    setOpen(true);
    triggerAnalytics(PROJECT_SHARE_CLICK, {});
  };

  const onSuccessRevised = (successMsg) => {
    setOpen(false);
    onSuccessMessage(successMsg);
  };
  const onFailureRevised = (onFailureMsg) => {
    setOpen(false);
    onFailureMessage(onFailureMsg);
  };

  const cssTertiaryButton = classNames(
    'sui-inline-flex sui-gap-2 sui-items-center sui-justify-center',
    'sui-font-bold sui-text-base sui-leading-normal',
    'sui-px-2 sui-py-2',
    'sui-rounded-base sui-cursor-pointer sui-transition-colors',
    'sui-rounded-lg sui-border-1 sui-border-solid',
    'sui-btn-tertiary sui-btn-tertiary-active sui-btn-tertiary-hover'
  );

  return (
    <div data-component="ShareProject">
      {showLink ? (
        <Link
          component="button"
          type="button"
          aria-label="Share Project"
          data-testid="share-project-link"
          onClick={openDrawer}
          ref={shareRef}
        >
          <div className="sui-flex sui-items-center sui-gap-2">
            <ShareWeb
              data-testid="share-icon"
              {...(iconSize && { size: iconSize })}
            />
            <span className="sui-hidden xl:@xl:sui-flex">Share</span>
          </div>
        </Link>
      ) : (
        <button
          type="button"
          className={buttonClassName || cssTertiaryButton}
          aria-label="Share Project"
          data-testid="share-project-button"
          onClick={openDrawer}
          ref={shareRef}
        >
          <ShareWeb {...(iconSize && { size: iconSize })} />
          {showText && 'Share'}
        </button>
      )}
      {open && (
        <ShareProjectDrawer
          open={open}
          setOpen={setOpen}
          project={project}
          onSuccessMessage={onSuccessRevised}
          onFailureMessage={onFailureRevised}
        />
      )}
    </div>
  );
};

ShareProject.displayName = 'ShareProject';

ShareProject.propTypes = {
  project: shape({
    id: string,
    defaultListId: string,
    name: string,
    public: bool
  }).isRequired,
  onSuccessMessage: func.isRequired,
  onFailureMessage: func.isRequired,
  showText: bool,
  showLink: bool,
  iconSize: string,
  buttonClassName: string
};

ShareProject.defaultProps = {
  showText: true,
  showLink: false,
  iconSize: null,
  buttonClassName: null
};

ShareProject.dataModel = extend(ShareProjectDrawer);
